/**
 * Order / Sales History services
 * Handles POS order processing, sales history, layaway, OMS orders, estimates, cart.
 */
import axios from "axios";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import useAuthStore from "../stores/useAuthStore";
import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";
import { API_TIMEOUTS } from "./api/client";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";
// Without a timeout, a dropped connection leaves the request (and any spinner
// awaiting it) hanging forever. axios.create() snapshots defaults, so the
// App.js global default set later never applies here.
retailInstance.defaults.timeout = API_TIMEOUTS.default;

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

const salesHistoryService = async (salesHistoryInput) => {
  try {
    const response = await retailInstance({
      data: salesHistoryInput,
    });
    return response;
  } catch (error) {
  }
};

const orderHistoryService = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in order history service");
  }
};

const posOrderProcessor1 = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in pos order processor service");
  }
};

const posOrderProcessor = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in posOrderProcessor:", error);
    return null;
  }
};

const confirmPOSLayawayOrder1 = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    // Rethrow (not swallow): the caller in useOrderProcessing.js reads
    // response.data.data.confirmPOSLayawayOrder1 with no optional chaining, so a
    // swallowed error already surfaced as a confusing generic TypeError via its
    // own catch block -- this makes that catch receive the real network/axios
    // error instead, with no change in control flow for the caller.
    console.error(error, "Error in confirm pos layaway order service");
    throw error;
  }
};

const confirmPOSLayawayOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in confirm pos layaway order service");
  }
};

const getNewOmsOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get new oms order service");
  }
};

const updateOmsOrder = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in update oms order service");
  }
};

const getNewOmsOrders = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get new oms orders");
  }
};

const updateOMSOrderStatus = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in update oms order status");
  }
};

const verifyReturn = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in verifyReturn:", error);
    return null;
  }
};

const verifyStock = async (data) => {
  try {
    const response = await retailInstance({ data });
    return response;
  } catch (error) {
    console.error("Error in verify stock:", error);
    return null;
  }
};

const upsertOfdOrderStatus = async (orderId, statusCode, reason, reasonCode, isOnline) => {
  try {
    const queryPayload = {
      query: `mutation {
            upsertOfdOrderStatus(order: [{
              sOrderID: "${orderId}"
              ofdStatus: "${statusCode}"
              ${reason ? `reasonCode: "${reason}"` : ""}
              ${reasonCode ? `reasonCode: "${reasonCode}"` : ""}
              metaData: []
              isOnline: "${isOnline}"
            }]) {
              status
              message
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert ofd order status");
  }
};

// TSK-3229: per-line stock + modifiability for one AK/FR/NEW aggregator order.
// Deliberately not verifyStock — that sums only the single default bin of
// cs_bunit.cwr_m_warehouse_id and ignores buffers, which would contradict the
// listing definition (ProductStockToggleBackgroundService) this badge follows.
const getAggregatorOrderStock = async (tillId, sOrderId) => {
  try {
    const queryPayload = {
      query: `query {
            getAggregatorOrderStock(tillId: "${tillId || ""}", sOrderId: "${sOrderId}") {
              sOrderId
              documentNo
              ofdStatus
              allowModification
              modifiable
              reason
              catalogueId
              catalogueCode
              items {
                sOrderlineId
                line
                mProductId
                productCode
                productName
                uom
                qty
                lineNet
                lineTax
                lineGross
                onhandQty
                bufferQty
                sellableQty
                availabilityStatus
                catalogueListed
                isBom
              }
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in getAggregatorOrderStock");
    return null;
  }
};

// Catch-up path behind the new-order popup. The live WebSocket event is fast
// but not guaranteed — any hop between retail and the browser can drop it while
// the order books normally, which is how popups have gone missing. This asks
// the database, which is the source of truth, what this till has not been told
// about. Called on connect, on reconnect and on a 30s timer, so a lost event
// costs at most one interval instead of the order.
//
// tillId here is the till_ref the popup is addressed by (UrbanPiper
// merchant_ref_id), not the cwr_till PK.
const getPendingOrderNotifications = async (tillId, bunitId, limit) => {
  try {
    const queryPayload = {
      query: `query {
            getPendingOrderNotifications(tillId: "${tillId || ""}", bunitId: "${bunitId || ""}", limit: ${limit || 20}) {
              notificationId
              sOrderId
              orderNo
              externalId
              tillId
              storeId
              state
              source
              created
              ageSeconds
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in getPendingOrderNotifications");
    return null;
  }
};

// Marks an online order as seen by the operator. Terminal — it is never offered
// by the poll again. Acknowledging twice is a success, not an error, so this is
// safe to retry.
const acknowledgeOrderNotification = async (tillId, sOrderId) => {
  try {
    const queryPayload = {
      query: `mutation {
            acknowledgeOrderNotification(tillId: "${tillId || ""}", sOrderId: "${sOrderId}") {
              type
              code
              message
              recordId
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in acknowledgeOrderNotification");
    return null;
  }
};

// TSK-3229: substitutes out-of-stock item(s) on an AK/FR aggregator order with
// replacement product(s), single-step (no approval workflow).
// TSK-4127: a lineChange can now also be a QTY_EDIT (cuts qty to what's in
// stock and frees the difference), and newLines spends what that frees on
// substitute items. Both extensions are optional — a REPLACE-only call is
// byte-for-byte what this sent before this feature existed. Numeric fields
// (newQty, qty, overrideUnitPrice) are BigDecimal on the schema and must NOT
// be quoted, or the mutation rejects the payload as a type mismatch.
const posAggregatorItemModify = async (tillId, sOrderId, lineChanges, newLines) => {
  try {
    const lineChangesGql = (lineChanges || [])
      .map((lc) => {
        const fields = [`sOrderlineId: "${lc.sOrderlineId}"`];
        if (lc.changeType) fields.push(`changeType: "${lc.changeType}"`);
        if (lc.newProductId) fields.push(`newProductId: "${lc.newProductId}"`);
        if (lc.newQty != null) fields.push(`newQty: ${lc.newQty}`);
        return `{ ${fields.join(", ")} }`;
      })
      .join(", ");
    const newLinesGql = (newLines || [])
      .map((nl) => {
        const fields = [`mProductId: "${nl.mProductId}"`, `qty: ${nl.qty}`];
        if (nl.overrideUnitPrice != null) fields.push(`overrideUnitPrice: ${nl.overrideUnitPrice}`);
        if (nl.lineGross != null) fields.push(`lineGross: ${nl.lineGross}`);
        if (nl.sourceLineId) fields.push(`sourceLineId: "${nl.sourceLineId}"`);
        return `{ ${fields.join(", ")} }`;
      })
      .join(", ");
    const queryPayload = {
      query: `mutation {
            posAggregatorItemModify(modification: {
              tillId: "${tillId || ""}"
              sOrderId: "${sOrderId}"
              lineChanges: [${lineChangesGql}]
              newLines: [${newLinesGql}]
            }) {
              status
              message
              recordId
              documentNo
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in posAggregatorItemModify");
    throw error;
  }
};

// TSK-4875: onBill-mode billing for an AK/FR aggregator order -- shipment,
// stock, invoice and receipt in one step, ofd status untouched. Idempotent:
// "200" BILLED / ALREADY_BILLED, "202" still generating, "201" refused.
const generateAggregatorBill = async (sOrderId) => {
  const queryPayload = {
    query: `mutation {
          generateAggregatorBill(orderId: "${sOrderId}") {
            status
            type
            message
            documentNo
            recordId
            id
          }
        }`,
  };
  return retailInstance({ data: queryPayload });
};

// TSK-3229: search the store's OWN aggregator catalogue (resolved server-side
// via cs_integration_datamapping, not cs_bunit.cwr_pcatalogue_id which is one
// shared catalogue for every store) for replacement products.
const getAggregatorCatalogueProducts = async (tillId, searchText, limit) => {
  try {
    const queryPayload = {
      query: `query {
            getAggregatorCatalogueProducts(
              tillId: "${tillId || ""}"
              searchText: "${(searchText || "").replace(/"/g, '\\"')}"
              limit: ${Number(limit) || 50}
            ) {
              cwrPcataloguelinesId
              mProductId
              productCode
              productName
              uom
              sellingPrice
              bufferQty
              onhandQty
              sellableQty
              availabilityStatus
              isactive
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in getAggregatorCatalogueProducts");
    throw error;
  }
};

const salesHistoryAggregator = async (sortData, filterbyData, tillValue) => {
  try {
    const queryPayload = {
      query: `query {salesHistory(
                      q: null,
                      filter_by: "${filterbyData}",
                      startRow: "0",
                      endRow: "100",
                      sort_by: "${sortData}",
                      tillId: "${tillValue?.cwr_till_id}"
                      aggregator: "Y",
                      layAway: "N"
                    ) {
                    sOrderID
                    documentno
                    dateordered
                    totalQty
                    orderTime
                    grosstotal
                    layAway
                    isReturn
                    ofdStatus
                    isBilled
                    shipmentStatus
                    orderInvoiceStatus
                    externalId
                    externalId2
                    saleType {
                        cwrSaletypeId
                        name
                        value
                      }
                    cwrB2cCustomer {
                      name
                      }
                    cwrTill {
                      till
                    }
                    metaData {
                    key
                    value
                    }
                  }
                  }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in aggregator sales history");
  }
};

const salesHistoryDetails = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in sales history details");
  }
};

const upsertOfdOrderPrintStatus = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert ofd order print status");
  }
};

// TSK-5163: the cancel reason is free text typed by the cashier; a quote, backslash or
// newline in it broke out of the GraphQL string literal and the whole mutation failed.
const escapeGraphQLString = (value) =>
  String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r\n|\r|\n/g, "\\n");

// TSK-5163: read the order's current server state right before Cancel Order, so the
// cancel is decided on what the server holds now, not on the list loaded earlier.
// paymentplan amounts sum to fin_receiptplan.paidamt, which is what cancelPreOrder
// refunds. deliveredQty is in the schema but not yet filled by salesHistoryDetails.
const getAdvanceOrderCancelState = async (sOrderID) => {
  const query = `query {
    salesHistoryDetails(orderId: "${escapeGraphQLString(sOrderID)}") {
      sOrderID
      documentno
      docstatus
      isinvoiced
      invoiceId
      invoiceNo
      finReceiptPlan {
        finReceiptPlanDetails {
          amount
        }
      }
      line {
        sOrderlineID
        deliveredQty
      }
    }
  }`;
  const response = await retailInstance({ data: { query } });
  if (response?.data?.errors?.length) {
    throw new Error(response.data.errors[0]?.message || "salesHistoryDetails failed");
  }
  const order = response?.data?.data?.salesHistoryDetails?.[0];
  if (!order) throw new Error("Order not found on the server");
  return order;
};

const cancelPreorder = async (sOrderID, cancelReason) => {
  try {
    const query = `
  mutation {
  cancelPreOrder(s_orderId: "${escapeGraphQLString(sOrderID)}", remarks: "${escapeGraphQLString(cancelReason)}"){
    status,
    message
  }
}
  `;

    const response = await retailInstance({
      data: { query },
    });

    return response?.data?.data?.cancelPreOrder;
  } catch (err) {
    console.error("Error in cancelPreOrder:", err);
    throw err;
  }
};

const upsertPOSKOT = async (receiptData, tillData) => {
  try {
    const vendors = receiptData.vendors || [];
    const results = [];

    for (const vendor of vendors) {
      const tokenId = uuidv4().replace(/-/g, "");
      const timestamp = new Date().toISOString();
      const userData = useAuthStore.getState().userData;
      const kotJson = JSON.stringify({
        tokenId,
        orderId: receiptData?.sOrderID || "",
        orderNo: receiptData.documentno,
        tokenNo: vendor?.token || "",
        productionCenter: vendor?.productionCenter || "",
        productionCenterId: vendor?.productionCenterId || "",
        updatedAt: timestamp,
        updatedBy: userData?.user || "",
        items: vendor?.items?.map((item) => ({
          qty: Math.floor(item.weight),
          productName: item.name,
          subProducts: item?.addons || [],
          preparationTime: item?.preparationTime || 1,
          notes: item?.notes || null,
          updatedAt: timestamp,
          updatedBy: userData?.user || "",
        })),
        orderDate: receiptData.orderDate,
        orderTime: receiptData.orderTime,
        customer: receiptData.customer,
        total: vendor.items.reduce((sum, item) => sum + (item.total || 0), 0),
        saleType: receiptData?.saleType || {},
        tableNo: receiptData?.tableNo || null,
        printerId: vendor?.printerId || null,
      });

      const queryPayload = {
        query: `mutation {
          upsertPOSKOT(kot: [{
            cwrFbKotId: "${tokenId}"
            bunitId: "${tillData.tillAccess.csBunit.csBunitId}"
            cwrFbProductionCenterId: ${vendor.productionCenter ? `"${vendor.productionCenter}"` : null}
            kotJson: ${JSON.stringify(kotJson)}
            kotNo: ${vendor.token ? `"${vendor.token}"` : null}
            orderNo: ${receiptData.documentno ? `"${receiptData.documentno}"` : null}
            tillId: "${tillData.tillAccess.cwrTill.cwrTillID}"
            salesRepId: ${receiptData.salesRepId ? `"${receiptData.salesRepId}"` : null}
          }]) {
            status
            message
          }
        }`,
      };

      const response = await retailInstance({
        data: queryPayload,
      });

      const result = response?.data?.data?.upsertPOSKOT;
      if (result?.status === "200") {
        results.push({ status: "Success", message: result.message, productionCenter: vendor.productionCenter, token: vendor.token });
      } else {
        console.error(`KOT insertion failed for ${vendor.productionCenter}:`, result?.message);
        results.push({ status: "Error", message: result?.message, productionCenter: vendor.productionCenter, token: vendor.token });
      }
    }

    const allSuccess = results.every((result) => result.status === "Success");
    if (allSuccess) {
      return { status: "Success", message: "All KOTs inserted successfully", results };
    } else {
      return { status: "Error", message: "One or more KOT insertions failed", results };
    }
  } catch (error) {
    console.error("Error inserting KOTs:", error);
    return { status: "Error", message: error.message, results: [] };
  }
};

const upsertCart = async (cartPayload) => {
  try {
    const queryPayload = {
      query: `
        mutation UpsertCart($cartJson: String!) {
          upsertCart(cartJson: $cartJson) {
            status
            message
          }
        }
      `,
      variables: {
        cartJson: JSON.stringify(cartPayload),
      },
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in upsertCart", error);
  }
};

const upsertStoreDiscount = async (upsertStoreDiscountPayload) => {
  try {
    const queryPayload = {
      query: `
        mutation UpsertStoreDiscount($discountData: String!) {
          upsertStoreDiscount(discountData: $discountData) {
            status
            message
          }
        }
      `,
      variables: {
        discountData: JSON.stringify(upsertStoreDiscountPayload),
      },
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in upsertStoreDiscount", error);
  }
};

const cartHistory = async (cartPayload) => {
  try {
    const queryPayload = {
      query: `
        query CartHistory(
          $q: String
          $filter_by: String
          $sort_by: String
          $tillId: String
        ) {
          cartHistory(
            q: $q
            filter_by: $filter_by
            sort_by: $sort_by
            tillId: $tillId
          )
        }
      `,
      variables: {
        q: cartPayload.q,
        filter_by: cartPayload.filter_by,
        sort_by: cartPayload.sort_by,
        tillId: cartPayload.tillId,
      },
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in cartHistory", error);
  }
};

const upsertEstimate = async (estimatePayload) => {
  try {
    const queryPayload = {
      query: `
        mutation UpsertEstimate($estimateJson: String!) {
          upsertEstimate(request: $estimateJson) {
            status
            message
          }
        }
      `,
      variables: {
        estimateJson: JSON.stringify(estimatePayload),
      },
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in upsertEstimate", error);
  }
};

const getEstimates = async (estimatePayload) => {
  try {
    const queryPayload = {
      query: `
        query GetEstimates(
          $bunitId: String
          $recordId: String
          $status: String
          $startRow: String
          $endRow: String
          $date: String
          $documentno: String
        ) {
          getEstimates(
            bunitId: $bunitId
            recordId: $recordId
            status: $status
            startRow: $startRow
            endRow: $endRow
            date: $date
            documentno: $documentno
          )
        }
      `,
      variables: {
        bunitId: estimatePayload?.bunitId,
        recordId: estimatePayload?.recordId,
        status: estimatePayload?.status,
        startRow: estimatePayload?.startRow,
        endRow: estimatePayload?.endRow,
        date: estimatePayload?.date,
        documentno: estimatePayload?.documentno,
      },
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in getEstimates", error);
  }
};

const RFIDTag = async (data) => {
  try {
    const query = {
      query: `mutation{
      RFIDTag(rfidTag:[${data}]){
      status
      message
      }
      }`,
    };
    const response = await retailInstance({ data: query });
    return response;
  } catch (error) {
    console.error(error, "Error in RFID tag service");
  }
};

const getInventoryProductsService = async (data) => {
  try {
    const response = await retailInstance({
      data: data,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in get inventory products");
  }
};

export {
  salesHistoryService,
  orderHistoryService,
  posOrderProcessor1,
  posOrderProcessor,
  confirmPOSLayawayOrder1,
  confirmPOSLayawayOrder,
  getNewOmsOrder,
  updateOmsOrder,
  getNewOmsOrders,
  updateOMSOrderStatus,
  verifyReturn,
  verifyStock,
  upsertOfdOrderStatus,
  getAggregatorOrderStock,
  getPendingOrderNotifications,
  acknowledgeOrderNotification,
  posAggregatorItemModify,
  generateAggregatorBill,
  getAggregatorCatalogueProducts,
  salesHistoryAggregator,
  salesHistoryDetails,
  upsertOfdOrderPrintStatus,
  cancelPreorder,
  getAdvanceOrderCancelState,
  upsertPOSKOT,
  upsertCart,
  upsertStoreDiscount,
  cartHistory,
  upsertEstimate,
  getEstimates,
  RFIDTag,
  getInventoryProductsService,
};
